import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CContainer, CRow, CCol, CCard, CCardBody, CSpinner, CButton, CBadge, CCollapse } from '@coreui/react';
import api from '../../api/axiosInstance';
import MathRenderer from '../../components/MathRenderer';
import { FaFacebookF, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import AnnouncementBanner from '../../components/AnnouncementBanner'; // Import AnnouncementBanner

const ViewQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [questionData, setQuestionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showSolution, setShowSolution] = useState(false);

  const handleToggleSolution = () => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    setShowSolution(!showSolution);
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=Check out this question: ${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };

  useEffect(() => {
    const fetchQuestionDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(`/api/Content/GetQuestionById/${id}`);
        setQuestionData(res.data);
      } catch (err) {
        console.error("Error fetching question details:", err);
        setError("Failed to load question details or question not found.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuestionDetails();
    }
  }, [id]);

  const renderContent = (rawHtml) => {
    if (!rawHtml || typeof rawHtml !== 'string') return '';

    const correctedHtml = rawHtml.replace(
      /src="\/uploads\//g,
      'src="https://apiambipie.mohdwebsolution.in/uploads/'
    );

    const mathRegex = /(\$\$[\s\S]+?\$\$|\$[\s\S]+?\$)/g;

    const renderMathText = (textStr) => {
      if (!textStr) return null;
      const parts = textStr.split(mathRegex);

      return parts.map((part, pIdx) => {
        if (!part) return null;
        if (
          (part.startsWith('$') && part.endsWith('$')) ||
          (part.startsWith('$$') && part.endsWith('$$'))
        ) {
          return <Latex key={pIdx}>{part}</Latex>;
        }
        return <span key={pIdx}>{part}</span>;
      });
    };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(correctedHtml, 'text/html');
      const childNodes = Array.from(doc.body.childNodes);

      return (
        <div className="view-question-detail-preview ck-content" style={{ lineHeight: '1.7' }}>
          {childNodes.map((node, idx) => {
            if (node.nodeName === 'IMG' || (node.querySelector && node.querySelector('img'))) {
              const imgTag = node.nodeName === 'IMG' ? node : node.querySelector('img');
              let imgSrc = imgTag.getAttribute('src') || '';

              return (
                <div
                  key={idx}
                  className="my-3 text-center p-2 border bg-light rounded d-block overflow-hidden"
                  style={{ maxWidth: '100%', width: '100%', margin: '15px auto' }}
                >
                  <img
                    src={imgSrc}
                    alt="Academic Visual"
                    style={{ maxHeight: '400px', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '4px' }}
                  />
                </div>
              );
            }

            const textVal = node.textContent || '';
            if (mathRegex.test(textVal)) {
              return (
                <div key={idx} style={{ margin: '6px 0', width: '100%' }}>
                  {renderMathText(textVal)}
                </div>
              );
            }

            return (
              <div
                key={idx}
                dangerouslySetInnerHTML={{ __html: node.outerHTML || node.textContent }}
                style={{ display: 'inline-block', width: '100%' }}
              />
            );
          })}
        </div>
      );
    } catch (err) {
      return <Latex>{correctedHtml}</Latex>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  if (error || !questionData) {
    return (
      <CContainer className="text-center mt-5 py-5">
        <h4 className="text-danger mb-3">{error || "Data load error"}</h4>
        <CButton color="primary" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  const isSubjectiveMode = (questionData.questionType || questionData.QuestionType) === 'Subjective';

  const activeOptions = [
    { key: 'optionA', label: 'A', value: questionData.optionA || questionData.OptionA },
    { key: 'optionB', label: 'B', value: questionData.optionB || questionData.OptionB },
    { key: 'optionC', label: 'C', value: questionData.optionC || questionData.OptionC },
    { key: 'optionD', label: 'D', value: questionData.optionD || questionData.OptionD }
  ];

  const optE = questionData.optionE || questionData.OptionE;
  if (optE && optE.toString().trim() !== '') {
    activeOptions.push({ key: 'optionE', label: 'E', value: optE });
  }

  return (
    <CContainer className="py-4">
      {/* Top Header Bar */}
      <div className="d-flex justify-content-between align-items-center p-3 text-white rounded-top shadow-sm" style={{ backgroundColor: 'rgb(0, 33, 71)' }}>
        <div className="d-flex align-items-center gap-3">
          <h4 className="m-0 fw-bold  " style={{ color: 'yellow' }}>Question Details</h4>
          <CBadge     className="p-2" style={{ fontSize: '0.85rem', color:'white', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px'  }}>
            ID: {questionData.customQuestionId || `NSRN01000${id}`}
          </CBadge>
        </div>
      </div>

      {/* Metadata Row */}
      <div className="bg-white border-bottom p-3 shadow-sm mb-4">
        <div className="text-muted small fw-bold mb-3 d-flex align-items-center gap-2">
          📌 ACADEMIC SYLLABUS METADATA TRACK
        </div>
        <CRow className="g-3 text-center">
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Chapter</div>
              <div className="fw-bold text-primary text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.chapterName || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Topic</div>
              <div className="fw-bold text-dark text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.topicName || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Asked In</div>
              <div className="fw-bold text-info text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.askedIn || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Asked Year</div>
              <div className="fw-bold text-warning" style={{ fontSize: '13px' }}>{questionData.year || questionData.askedYear || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Book Reference</div>
              <div className="fw-bold text-secondary text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.bookReference || 'N/A'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Question Type</div>
              <div className="fw-bold text-danger text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.questionType || 'Objective'}</div>
            </div>
          </CCol>
          <CCol xs={6} sm={4} md={3} lg={2}>
            <div className="border p-2 rounded bg-light shadow-xs h-100">
              <div className="text-muted small text-uppercase fw-bold mb-1" style={{ fontSize: '11px' }}>Complexity</div>
              <div className="fw-bold text-success text-truncate px-1" style={{ fontSize: '13px' }}>{questionData.complexity || 'Easy'}</div>
            </div>
          </CCol>
        </CRow>
      </div>

      {/* Action Row */}
      <div className="bg-white border rounded p-3 shadow-sm mb-4">
        <CRow className="align-items-center g-3">
          <CCol xs={12} sm={6} className="d-flex align-items-center gap-2">
            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Social Media Icon to Share:</span>
            <div className="d-flex gap-2">
              <button onClick={() => handleShare('facebook')} className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaFacebookF size={14} /></button>
              <button onClick={() => handleShare('whatsapp')} className="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaWhatsapp size={15} /></button>
              <button onClick={() => handleShare('twitter')} className="btn btn-sm btn-outline-dark d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: '0' }}><FaXTwitter size={14} /></button>
            </div>
          </CCol>
          <CCol xs={12} sm={6} className="d-flex align-items-center gap-2 justify-content-sm-end">
            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Download this as PDF:</span>
            <button onClick={() => navigate(`/content/download-pdf/${id}`)} className="btn btn-sm btn-danger py-2 px-3 fw-bold d-flex align-items-center gap-2 shadow-sm" style={{ borderRadius: '6px' }}>📄 Download PDF</button>
          </CCol>
        </CRow>
      </div>

      {/* Core Question Content */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardBody className="p-4 bg-white rounded border">
          <div className="mb-4">
            <h5 className="fw-bold text-primary d-flex align-items-center gap-2 mb-3">❓ Question Statement:</h5>
            <div className="p-3 bg-light rounded text-dark border-start border-primary border-4 shadow-xs overflow-hidden" style={{ fontSize: '1.2rem', lineHeight: '1.6' }}>
              <MathRenderer htmlContent={questionData.questionText || questionData.QuestionText} />
            </div>
          </div>

          {/* Dynamic Options Mapping Section */}
          {!isSubjectiveMode && (
            <div className="mb-5">
              <h5 className="fw-bold text-secondary d-flex align-items-center gap-2 mb-3">🎯 Options Array Matrix:</h5>
              <CRow className="g-3">
                {activeOptions.map((opt) => {
                  const colWidth = opt.key === 'optionE' ? 12 : 6;
                  return (
                    <CCol xs={12} md={colWidth} key={opt.key}>
                      <div className="d-flex align-items-center gap-2 border p-3 rounded bg-white shadow-xs w-100 h-100 overflow-hidden" style={{ borderLeft: '4px solid #ced4da' }}>
                        <span className="fw-bold text-primary px-1" style={{ fontSize: '1.1rem' }}>({opt.label})</span>
                        <div className="flex-grow-1 text-dark m-0 p-0 d-inline-block align-middle overflow-hidden" style={{ width: 'calc(100% - 35px)' }}>
                          {renderContent(opt.value)}
                        </div>
                      </div>
                    </CCol>
                  );
                })}
              </CRow>
            </div>
          )}
<AnnouncementBanner position="between-q-sol" />
          {/* Dynamic Solution Section */}
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold text-success m-0 d-flex align-items-center gap-2">
                💡 Step-by-Step Solution / Proof:
              </h5>

              <CButton 
                color={showSolution ? "secondary" : "success"}
                size="sm"
                className="fw-bold text-white px-3 py-1.5 shadow-sm"
                onClick={handleToggleSolution}
              >
                {showSolution ? '🙈 Hide Solution' : '👁️ View Solution'}
              </CButton>
            </div>

            {/* Solution Block */}
            <CCollapse visible={showSolution}>
              <div className="p-3 rounded border-start border-success border-4 shadow-xs overflow-hidden" style={{ backgroundColor: '#f4faf6', minHeight: '100px', fontSize: '1.15rem' }}>
                {(questionData.solution || questionData.Solution) ? (
                  renderContent(questionData.solution || questionData.Solution)
                ) : (
                  <span className="text-muted italic">No structural solution logic submitted yet.</span>
                )}
              </div>
            </CCollapse>
          </div>
        </CCardBody>
      </CCard>

      <div className="d-flex justify-content-start gap-2">
        <CButton color="secondary" className="px-4 fw-bold shadow-sm" onClick={() => navigate(-1)}>← Back to Content List</CButton>
      </div>

      <style>{`
        .view-question-detail-preview img,
        .ck-content img,
        .katex-display img {
          max-width: 100% !important;
          height: auto !important;
          object-fit: contain !important;
          display: block;
          margin: 10px auto;
        }
        .ck-content, .view-question-detail-preview {
          overflow-x: auto;
          max-width: 100%;
        }
      `}</style>
    </CContainer>
  );
};

export default ViewQuestion;